<template>
  <div v-if="mounted" class="dense-table">
    <v-data-table
      :items="getFilteredProducts"
      item-key="id"
      :headers="getHeaders"
      :sort-by="
        getContainsLocalisationTag === true && useOrder === true
          ? 'category_tag_order'
          : 'date'
      "
      :sort-desc="
        getContainsLocalisationTag === true && useOrder === true ? false : true
      "
      :loading="loading"
      :mobile-breakpoint="0"
      :search="searchProps !== undefined ? searchProps : search"
      :items-per-page="infinite ? getProducts.length + 1 : 10"
      :hide-default-header="hideHeader || $vuetify.breakpoint.xsOnly"
      :custom-filter="customFilter"
      hide-default-footer
      dense
      @page-count="pageCount = $event"
      :page.sync="page"
    >
      <template v-slot:item.modify="{ item }">
        <v-row no-gutters align="center">
          <v-btn
            icon
            v-if="getUseAddedValue(item) === false"
            @click="toggleUseAddedValue(item)"
          >
            <v-icon>fa-calculator</v-icon>
          </v-btn>
          <v-btn
            icon
            color="success"
            v-else
            @click="updateQuantityWithAddedValue(item)"
          >
            <v-icon>fa-save</v-icon>
          </v-btn>
          <v-btn
            icon
            color="error"
            v-if="getUseAddedValue(item) === true"
            @click="toggleUseAddedValue(item)"
          >
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-row>
      </template>
      <template v-slot:item.infos="{ item }">
        <InventoryGridInfosDialog :item="item"></InventoryGridInfosDialog>
      </template>
      <template v-slot:item.nom="{ item }">
        <span v-if="dense" style="width: 250px;">{{ item.nom }}</span>
        <v-row v-else-if="loading" align="center" dense>
          <v-col cols="12">
            <v-progress-linear
              class="mx-1"
              :height="52"
              style="min-width: 260px;"
              color="tertiary"
              rounded
            ></v-progress-linear>
          </v-col>
        </v-row>
        <InventoryItemAdder
          v-else-if="getUseAddedValue(item) === true"
          :item="item"
        ></InventoryItemAdder>
        <v-text-field
          v-else
          class="font-capitalize no-before inventory-input my-1 align-end"
          :ref="item.nom"
          :label="item.nom"
          filled
          dense
          type="number"
          min="0"
          :value="getQuantity(item)"
          :readonly="readOnly"
          @change="v => updateQuantity(item, v)"
          @focus="onFocus"
          hide-details
          aria-autocomplete="off"
          autocomplete="off"
        >
          <template v-slot:append>
            <span class="caption">
              {{
                item.data && item.data.unite_mesure_inventory
                  ? item.data.unite_mesure_inventory
                  : item.data
                  ? item.data.unite_mesure
                  : ""
              }}
            </span>
          </template>
        </v-text-field>
      </template>
      <template v-slot:item.quantity="{ item }">
        <span>{{ getQuantity(item) }}</span>
        <span class="caption">
          {{
            item.data && item.data.unite_mesure_inventory
              ? item.data.unite_mesure_inventory
              : item.data
              ? item.data.unite_mesure
              : ""
          }}</span
        >
      </template>
      <template v-slot:item.value="{ item }">
        <span>{{ getItemValue(item) }}</span>
        <span class="caption ml-1">$</span>
      </template>
      <template v-slot:item.date="{ item }">
        <span class="caption mr-2">{{ getDate(item) }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-row justify="end" no-gutters v-if="getIsAdmin">
          <v-btn
            icon
            :small="!$vuetify.breakpoint.xsOnly"
            @click="goToEditPage(item)"
          >
            <v-icon :small="!$vuetify.breakpoint.xsOnly"
              >far fa-file-alt</v-icon
            >
          </v-btn>
        </v-row>
      </template>
      <template v-slot:top v-if="showSearch">
        <v-row class="no-print" align="center" justify="end" dense>
          <v-col cols="12" sm="6">
            <v-text-field
              dense
              filled
              hide-details
              single-line
              clearable
              prepend-inner-icon="fa-search"
              v-model="search"
              class="no-before"
            ></v-text-field>
          </v-col>
        </v-row>
      </template>
      <template v-slot:footer v-if="!infinite">
        <v-pagination
          class="no-print"
          color="#757575"
          v-model="page"
          :length="pageCount"
          :total-visible="5"
        ></v-pagination>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { api } from "@/api";
import { mappers } from "@/mappers";
import moment from "moment";
import round10 from "round10";
import { setTimeout } from "timers";
import InventoryGridInfosDialog from "./InventoryGridInfosDialog";
import InventoryItemAdder from "./InventoryItemAdder";
export default {
  name: "InventoryGrid",
  components: {
    InventoryGridInfosDialog,
    InventoryItemAdder
  },
  props: {
    filter: {
      type: Array,
      required: true
    },
    records: {
      type: Array,
      required: false,
      default: () => []
    },
    infinite: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    manualRecords: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    hideTop: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    useOrder: {
      type: Boolean,
      default: false
    },
    searchProps: {
      default: undefined
    }
  },
  computed: {
    ...mapGetters([
      "getInventoryTransactions",
      "getProducts",
      "getStoreId",
      "getIsAdmin",
      "getFirstInventoryEdit",
      "getTagCategories",
      "getInventoryTagOrders"
    ]),
    getInventoryRecords() {
      if (this.manualRecords) {
        return this.records;
      } else {
        return this.$store.getters.getInventoryRecords;
      }
    },
    getContainsLocalisationTag() {
      let contain = false;
      const localisationTags = [
        ...new Set(
          ...this.getTagCategories
            .filter(x => x.category_type === 1)
            .map(x => x.tags)
        )
      ];
      this.filter.forEach(x => {
        if (localisationTags.includes(x)) {
          contain = true;
        }
      });
      return contain;
    },
    getLocalisationTag() {
      let tag = null;
      const localisationTags = [
        ...new Set(
          ...this.getTagCategories
            .filter(x => x.category_type === 1)
            .map(x => x.tags)
        )
      ];
      this.filter.forEach(x => {
        if (localisationTags.includes(x)) {
          tag = x;
        }
      });
      return tag;
    },
    getFilteredProducts() {
      if (this.filter.length > 0) {
        const items = this.getProducts.filter(
          x =>
            x.is_deleted === false &&
            x.data !== null &&
            this.filter.every(t =>
              x.data.tags
                .map(tag => mappers.normalize(tag))
                .includes(mappers.normalize(t))
            )
        );
        if (
          this.useOrder === true &&
          this.getContainsLocalisationTag === true
        ) {
          return items.map(x => {
            const tagOrder = this.getProductTagOrder(x);
            return {
              ...x,
              category_tag_order: tagOrder.order,
              category_tag_order_data: tagOrder
            };
          });
        }
        return items;
      } else {
        return this.infinite ? [] : this.getProducts;
      }
    },

    getHeaders() {
      if (this.$vuetify.breakpoint.xsOnly) {
        if (this.dense === true && this.getIsAdmin === true) {
          return [
            { value: "nom", text: "Produit" },
            { value: "quantity", text: "Quantité" },
            { value: "value", text: "Valeur" }
          ];
        } else if (this.dense === true) {
          return [
            { value: "nom", text: "Produit" },
            { value: "quantity", text: "Quantité" }
          ];
        } else {
          return [
            { value: "infos", text: "", class: "px-0" },
            { value: "nom", text: "Produit" },
            { value: "modify", text: "", width: 50 }
          ];
        }
      } else {
        if (this.dense === true && this.getIsAdmin === true) {
          return [
            { value: "nom", text: "Produit", width: 300 },
            { value: "quantity", text: "Quantité", width: 150 },
            { value: "value", text: "Valeur", width: 150 }
          ];
        } else if (this.dense === true) {
          return [
            { value: "nom", text: "Produit", width: 300 },
            { value: "quantity", text: "Quantité", width: 150 }
          ];
        } else {
          return [
            { value: "infos", text: "", width: 75 },
            { value: "nom", text: "Produit", width: 250 },
            { value: "modify", text: "", width: 50 },
            {
              value: "date",
              text: "Dernière vérification",
              align: "end",
              width: 300
            }
          ];
        }
      }
    }
  },
  data() {
    return {
      mounted: true,
      saving: false,
      search: "",
      page: 1,
      pageCount: 0,
      containsLocalisationTag: false,
      localisationTag: null
    };
  },
  methods: {
    onFocus(event) {
      event.preventDefault();
      event.target.focus();
      event.target.select();
    },
    getQuantity(item) {
      const record = this.getInventoryRecords.find(
        x => x.fk_product === item.id
      );
      if (record) {
        return round10.round10(record.computedQuantity, -2);
      } else {
        return 0;
      }
    },
    getDate(item) {
      const record = this.getInventoryRecords.find(
        x => x.fk_product === item.id
      );
      if (record) {
        return moment(record.date).format("LLL");
      } else {
        return "";
      }
    },
    getItemValue(item) {
      const record = this.getInventoryRecords.find(
        x => x.fk_product === item.id
      );
      if (record) {
        const price = item.data.prix_inventory
          ? item.data.prix_inventory
          : item.data.prix;
        return round10.round10(this.getQuantity(item) * price, -2);
      }
    },
    getUseAddedValue(item) {
      const record = this.getInventoryRecords.find(
        x => x.fk_product === item.id
      );
      if (record) {
        return record.useAddedValue;
      } else {
        return false;
      }
    },
    toggleUseAddedValue(item) {
      const record = this.getInventoryRecords.find(
        x => x.fk_product === item.id
      );
      if (record) {
        record.useAddedValue = !record.useAddedValue;
      }
    },
    refreshInventories() {},
    async updateQuantityWithAddedValue(item) {
      const record = this.getInventoryRecords.find(
        x => x.fk_product === item.id
      );
      if (record) {
        const nv1 = record.v1 !== null ? Number(record.v1) : 0;
        const nv2 = record.v2 !== null ? Number(record.v2) : 0;
        const nv3 = record.v3 !== null ? Number(record.v3) : 0;
        const nv4 = record.v4 !== null ? Number(record.v4) : 0;
        const newValue = nv1 + nv2 + nv3 + nv4;
        await this.updateQuantity(item, newValue);
      }
    },
    async updateQuantity(item, value) {
      try {
        this.saving = true;
        if (this.getFirstInventoryEdit) {
          const allow = await this.$dialog.confirm({
            text: "Voulez-vous vraiment modifier cette quantité en inventaire?",
            title: "Modification d'inventaire"
          });
          if (!allow) {
            this.mounted = false;
            this.saving = false;
            setTimeout(() => (this.mounted = true), 10);
            return;
          } else {
            this.$store.dispatch("makeFirstInventoryEdit");
          }
        }
        value =
          value === null || value === undefined || value === "" ? 0 : value;
        const new_value = isNaN(value)
          ? parseFloat(value.replace(/,/g, "."))
          : value;
        if (this.getQuantity(item) != new_value) {
          const data = {
            fk_product: item.id,
            store: this.getStoreId,
            date: moment(new Date(Date.now())).format(
              "YYYY-MM-DDTHH:mm:ss.SSSSSSZ"
            ),
            quantity: new_value,
            is_deleted: false
          };
          this.$emit("update", data);
          this.$store.dispatch("addInventoryRecordHistory", data);
          const res = await api.postInventoryRecord(data);
          this.$store.dispatch("updateInventoryRecord", res);
        }
      } finally {
        if (this.$refs[item.nom] !== undefined) {
          this.$refs[item.nom].blur();
        }
        this.saving = false;
      }
    },
    goToEditPage(item) {
      if (
        item.est_intermediaire &&
        this.getRecipes.find(x => x.fk_produit_intermediaire === item.id)
      ) {
        this.$router.push({
          name: "RecipeEditor",
          params: { id: this.getRecipes.find(x => x.fk_product === item.id).id }
        });
      } else {
        this.$router.push({ name: "ProductEditor", params: { id: item.id } });
      }
    },
    getProductTagOrder(product) {
      const tagOrder = this.getInventoryTagOrders.find(
        x => x.product === product.id && x.tag === this.getLocalisationTag
      );
      if (tagOrder) {
        return tagOrder;
      } else {
        return {
          order: null
        };
      }
    },
    customFilter(value, search, item) {
      if (search !== null) {
        const normalizeSearch = search
          .toLowerCase()
          .replace(/\s/g, "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        const itemName = item.nom
          .toLowerCase()
          .replace(/\s/g, "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");

        if (itemName.indexOf(normalizeSearch) !== -1) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
